import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardMedia,
    Stack,
    Chip,
    CircularProgress,
    Alert
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { getArticleBySlug } from "../../Services/ApiCalls";
import type { Article } from "../../types";
import GoogleAd from "../../Components/GoogleAd";
import { AD_CONFIG } from "../../config/ads";
import Seo from "../../Components/Seo";
import SponsorAd from "../../Components/SponsorAd";

export default function ArticleView() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!slug) {
                setError('Article slug is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                console.log('Fetching article with slug:', slug);
                const data = await getArticleBySlug(slug);
                console.log('Article data received:', data);
                if (!data || !data.article) {
                    throw new Error('Article not found');
                }

                // Deduplicate figures within sections
                const article = data.article;
                if (article.sections && Array.isArray(article.sections)) {
                    article.sections = article.sections.map(section => {
                        if (!section.figures || !Array.isArray(section.figures)) {
                            return section;
                        }

                        // Deduplicate figures by ID, or by order+imageUrl if no ID
                        const seenFigures = new Map();
                        const uniqueFigures = [];
                        for (const figure of section.figures) {
                            if (figure.id && !seenFigures.has(figure.id)) {
                                seenFigures.set(figure.id, true);
                                uniqueFigures.push(figure);
                            } else if (!figure.id) {
                                // If no ID, check by order + imageUrl to avoid duplicates
                                const key = `${figure.order}-${figure.imageUrl || ''}`;
                                if (!seenFigures.has(key)) {
                                    seenFigures.set(key, true);
                                    uniqueFigures.push(figure);
                                }
                            }
                        }
                        return { ...section, figures: uniqueFigures };
                    });
                }

                setArticle(article);
            } catch (err) {
                console.error('Error fetching article:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to load article';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
    }, [slug]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !article) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Article not found'}
                </Alert>
                <Button variant="outlined" onClick={() => navigate('/articles')}>
                    Back to Articles
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 2 } }}>
            <Seo
                title={article.title}
                description={article.excerpt || `Read ${article.title} on Radiology Illustration, an educational radiology resource by Dr. Shubham Arora.`}
                canonicalPath={`/articles/${article.slug}`}
                type="article"
                image={article.coverImage}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: article.title,
                    description: article.excerpt || undefined,
                    image: article.coverImage || undefined,
                    author: {
                        '@type': 'Person',
                        name: article.author || 'Dr. Shubham Arora',
                    },
                    publisher: {
                        '@type': 'Organization',
                        name: 'Radiology Illustration',
                    },
                    datePublished: article.publishedAt || undefined,
                    mainEntityOfPage: `https://radiologyillustration.com/articles/${article.slug}`,
                }}
            />
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/articles')}
                sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                size="small"
            >
                Back to Articles
            </Button>

            <Card>
                {article.coverImage && (
                    <CardMedia
                        component="img"
                        image={article.coverImage}
                        alt={article.title}
                        sx={{
                            height: { xs: 200, sm: 300, md: 400 },
                            objectFit: "cover"
                        }}
                    />
                )}
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 2 }}
                        flexWrap="wrap"
                    >
                        <Chip
                            label="Published"
                            color="success"
                            size="small"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                        {article.publishedAt && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                                {formatDate(article.publishedAt)}
                            </Typography>
                        )}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                            • {article.readingTime} min read
                        </Typography>
                    </Stack>

                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            fontSize: {
                                xs: '1.5rem',
                                sm: '2rem',
                                md: '2.5rem',
                            },
                            lineHeight: 1.2,
                        }}
                    >
                        {article.title}
                    </Typography>

                    {article.excerpt && (
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{
                                mb: 3,
                                fontStyle: 'italic',
                                fontSize: {
                                    xs: '1rem',
                                    sm: '1.25rem',
                                },
                                lineHeight: 1.4,
                            }}
                        >
                            {article.excerpt}
                        </Typography>
                    )}

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            mb: 4,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                    >
                        By {article.author}
                    </Typography>

                    {/* Ad after article header - Only show if there is content */}
                    {article.sections && article.sections.length > 0 && (
                        <GoogleAd
                            adSlot={AD_CONFIG.adUnits.articleHeader}
                            format="horizontal"
                            showPlaceholder={false}
                        />
                    )}

                    {article.sections && article.sections.length > 0 ? (
                        <Box>
                            {article.sections
                                .sort((a, b) => a.order - b.order)
                                .map((section, index) => (
                                    <Box key={section.id} sx={{ mb: { xs: 3, sm: 4 } }}>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                fontSize: {
                                                    xs: '1.25rem',
                                                    sm: '1.5rem',
                                                    md: '1.75rem',
                                                },
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {section.title}
                                        </Typography>

                                        {/* Sponsored banner between the first and second article sections */}
                                        {index === 1 && (
                                            <SponsorAd my={{ xs: 2, sm: 3 }} placement="article_between_sections" />
                                        )}

                                        {section.bodyHtml ? (
                                            <Box
                                                dangerouslySetInnerHTML={{ __html: section.bodyHtml }}
                                                sx={{
                                                    '& p': {
                                                        mb: 2,
                                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                                        lineHeight: 1.6,
                                                    },
                                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        mt: 2,
                                                        fontSize: {
                                                            xs: '1.1rem',
                                                            sm: '1.25rem',
                                                            md: '1.5rem',
                                                        },
                                                    },
                                                    '& ul, & ol': {
                                                        pl: { xs: 2, sm: 3 },
                                                        mb: 2
                                                    },
                                                    '& li': {
                                                        mb: 0.5,
                                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                                        lineHeight: 1.6,
                                                    },
                                                    '& img': {
                                                        maxWidth: '100%',
                                                        height: 'auto',
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {section.bodyMarkdown}
                                            </Typography>
                                        )}

                                        {section.figures && section.figures.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                {section.figures
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((figure) => (
                                                        <Box key={figure.id} sx={{ mb: 2, textAlign: 'center' }}>
                                                            <img
                                                                src={figure.imageUrl}
                                                                alt={figure.altText || figure.caption || ''}
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    height: 'auto',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                            {figure.caption && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                                    {figure.caption}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            This article doesn't have any content sections yet.
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
